import { environment } from '../../../environments/environment';

/**
 * Kod enum ID yapılandırma dosyası
 * Hem enum key'leri hem de ID mappingi tek yerde
 * Development sırasında bu dosyayı edit edip save ederek hot reload yapılabilir
 */

export interface KodEnumConfig {
  [key: number]: number;
}

/**
 * Enum key'leri - tüm ortamlar için aynı
 * Template'lerde kullanım: getKodId(KOD_ENUMS_KEYS.SERBEST_BULUS)
 */
export enum KOD_ENUMS_KEYS {
  SERBEST_BULUS,
  YENI_BULUS_BILDIRIMI,
  BULUS_TESCIL_EDILDI,
  BTYK_ARASTIRMA_ALANLARI,
  SORU_TIP,
  COKTAN_SECMELI,
  KLASIK,
  COKTAN_SECMELI_ANKET,
  COKLU_CHECK,
  COKTAN_SECMELI_ANKET_ARTI_KLASIK,
  ANA_SORU_BAGLI_ALT_SORULAR_VAR,
  KONTROLLERI_ATLA_ROL_ID,
  PATENT_DEGERLENDIRME_YETKILI_ROL_ID,
  FIRMA_LOGIN_TIP,
  FIRMA_ASAMA_KOD_TIP,
  KOD_ASAMA_DURUM_KOD_TIP,
  KOD_ASAMA_YETKILI_KOD_TIP,
  ODEME_DURUM_KOD_TIP,
  YENI_KULLANICI_ROL_ID,
  BULUS_NITELIGI_KOD_TIP,
  ONGORULEN_TESCIL_TURU_KOD_TIP,
  PATENT_BASVURU_ASAMA_KOD_TIP,
  RAPOR_VERI_LISTELERI_KOD_TIP,
  RAPOR_ISTATISTIK_LISTELERI_KOD_TIP,
  CEVAP_TIP_KOD_TIP,
  // Yeni enum key'leri buraya eklenecek
}

/**
 * Patent Basvuru Ana Asama Tip kodlari (sabit degerler, environment'a bagimsiz)
 * Backend: PATENT_BASVURU_ANA_ASAMA_TIP_CHILD
 */
export const PATENT_ANA_ASAMA_TIP = {
  BULUSCU_ASAMASI: 3120,
  IU_ASAMASI: 3121,
  FIRMA_ASAMASI: 3122
} as const;


/**
 * Gündem (kurul) aşama kodlari — t_kod TipId = 3132, t_gundem.GundemAsamaKID.
 * Backend: AppEnums.GUNDEM_ASAMA_TIP (aynı ID'ler).
 *
 * Geçiş yalnızca ileriye ve sıralıdır:
 *   HAZIRLANIYOR -> HAZIRLIK_TAMAMLANDI -> KARAR_GIRILDI -> ONAYLANDI
 * Sıralı olduğu için ">=" ile durum karşılaştırması yapılabilir; ARTAN sıra korunmalı.
 *
 * Ekranda gösterilen METİN buradan DEĞİL, t_kod kaydından (gundemAsamaKodDto.kod)
 * gelir. Buradaki adlar yalnızca kod içi karşılaştırma içindir.
 */
export const GUNDEM_ASAMA = {
  HAZIRLANIYOR: 3133,
  HAZIRLIK_TAMAMLANDI: 3134,
  KARAR_GIRILDI: 3135,
  ONAYLANDI: 3136
} as const;

/**
 * Veri Listeleri raporlarının kod id'leri — t_kod TipId = 3146.
 * Backend: AppEnums.RAPOR_VERI_LISTELERI_CHILD (aynı ID'ler).
 *
 * Ortamdan bağımsız sabitler (GUNDEM_ASAMA ile aynı desen): kolon ve filtre
 * yapılandırması bu anahtarlarla eşleştiği için şablonlara literal yazılmaz.
 *
 * Combobox'ta GÖRÜNEN METİN buradan DEĞİL, t_kod kaydından (kisaAd) gelir.
 */
export const RAPOR_VERI_LISTESI = {
  PATENT_DETAY: 3147,
  PATENT_ASAMA: 3148,
  FIRMA_ATAMA: 3149,
  BULUSCU_BAZLI: 3150,
  FAKULTE_BAZLI: 3151,
  KORUMA_SURESI: 3152
} as const;

/**
 * İstatistik raporlarının kod id'leri — t_kod TipId = 3153.
 * Backend: AppEnums.RAPOR_ISTATISTIK_LISTELERI_CHILD (aynı ID'ler).
 *
 * RAPOR_VERI_LISTESI ile aynı desen ve aynı gerekçe: rapor başına filtre yapılandırması
 * bu anahtarlarla eşleştiği için şablonlara literal yazılmaz.
 *
 * Combobox'ta GÖRÜNEN METİN buradan DEĞİL, t_kod kaydından (kisaAd) gelir.
 * Satırlar sql/2026-08-25-rapor-istatistik-kod.sql ile eklenir; yoksa combobox boş gelir.
 */
export const RAPOR_ISTATISTIK = {
  GENEL: 3154,
  ASAMA_BAZLI: 3155,
  BIRIM_BAZLI: 3156,
  FIRMA_ATAMA: 3157,
  TESCIL_DURUMU: 3158,
  BULUSCU_BAZLI: 3159
} as const;

/**
 * Patent değerlendirmesinin cevaplanma durumu — t_kod TipId = 3075 (CEVAPTIP),
 * t_patent_degerlendirme.CevaplanmaDurumKID.
 *
 * Değerlendirmeyi yapan kişi "cevap bekleniyor mu" sorusunu bu kodlarla yanıtlar;
 * başvuru sahibi cevabı verince kayıt CEVAP_ALINDI'ya geçer.
 *
 * Ortamdan bağımsız sabitler (GUNDEM_ASAMA ile aynı desen): daha önce 3077/3078
 * literalleri dört ayrı şablona/bileşene dağılmıştı.
 *
 * Ekranda GÖRÜNEN METİN buradan DEĞİL, t_kod kaydından (cevaplanmaDurumKodDTO.kod) gelir.
 */
export const CEVAPLANMA_DURUM = {
  /** Cevap istenmiyor — değerlendirme bilgi amaçlı. */
  CEVAP_BEKLENMIYOR: 3076,
  /** Başvuru sahibinden cevap bekleniyor. */
  CEVAP_BEKLENIYOR: 3077,
  /** Başvuru sahibi cevabını gönderdi. */
  CEVAP_ALINDI: 3078
} as const;

/**
 * Bir aşama geçişini KİM yaptı — t_patent_BasvuruDegerlendirme.UserYetkiKID.
 * t_kod TipId = 3119 (aşamada yetkilendirilen kişiler); backend tarafındaki karşılığı
 * AppEnums.PATENT_BASVURU_ANA_ASAMA_TIP_CHILD.
 *
 * Değerlendirme geçmişi kartının ikonu ve rengi bu id'den seçilir; ETİKET METNİ
 * kodun kendisinden (userYetkiKodDTO.kod) gelir, burada tutulmaz.
 */
export const USER_YETKI = {
  /** Başvuru sahibi (buluşçu) — başvuru sihirbazından yapılan geçişler. */
  BASVURU_SAHIBI: 3120,
  /** Patent ofisi personeli. */
  PATENT_OFISI: 3121,
  /** Firma çalışanı. */
  FIRMA_CALISANI: 3122,
  /** Kurul (gündem) kararı. */
  GUNDEM_SONUCU: 3161
} as const;

/**
 * Test ortamı için kod ID'leri
 * Development sırasında bu değerleri değiştirip save ederek anında test edilebilir
 */
const TEST_KOD_ENUMS: KodEnumConfig = {
  [KOD_ENUMS_KEYS.SERBEST_BULUS]: 2056,                    // bulusKullanimTalebiBeyaniCodeDto
  [KOD_ENUMS_KEYS.YENI_BULUS_BILDIRIMI]: 1978,             // bulusNiteligiCodeDto - TPE alanları gizlemek için
  [KOD_ENUMS_KEYS.BULUS_TESCIL_EDILDI]: 1980,             // bulusNiteligiCodeDto - TPE alanları gizlemek için

  [KOD_ENUMS_KEYS.BTYK_ARASTIRMA_ALANLARI]: 1504,          // BTYK Araştırma alanları tree select
  [KOD_ENUMS_KEYS.SORU_TIP]: 2009,
  [KOD_ENUMS_KEYS.COKTAN_SECMELI]: 2010,
  [KOD_ENUMS_KEYS.KLASIK]: 2011,
  [KOD_ENUMS_KEYS.COKTAN_SECMELI_ANKET]: 2012,
  [KOD_ENUMS_KEYS.COKLU_CHECK]: 2013,
  [KOD_ENUMS_KEYS.COKTAN_SECMELI_ANKET_ARTI_KLASIK]: 2014,
  [KOD_ENUMS_KEYS.ANA_SORU_BAGLI_ALT_SORULAR_VAR]: 2067,        // soruTipKodDto - ANA_SORU_BAGLI_ALT_SORULARI_VAR
  [KOD_ENUMS_KEYS.KONTROLLERI_ATLA_ROL_ID]: 14,
  [KOD_ENUMS_KEYS.PATENT_DEGERLENDIRME_YETKILI_ROL_ID]: 10,
  [KOD_ENUMS_KEYS.FIRMA_LOGIN_TIP]: 3118,
  [KOD_ENUMS_KEYS.FIRMA_ASAMA_KOD_TIP]: 2028,
  [KOD_ENUMS_KEYS.KOD_ASAMA_DURUM_KOD_TIP]: 2028,    // Kod asama durum kodları tip ID
  [KOD_ENUMS_KEYS.KOD_ASAMA_YETKILI_KOD_TIP]: 3119,  // Kod asama yetkili kişi kodları tip ID
  [KOD_ENUMS_KEYS.ODEME_DURUM_KOD_TIP]: 3124,       // Ödeme durum kodları tip ID
  [KOD_ENUMS_KEYS.YENI_KULLANICI_ROL_ID]: 7,        // Yeni Kullanıcı rolü (backend AppEnums.ROLE.YeniKullanici)
  [KOD_ENUMS_KEYS.BULUS_NITELIGI_KOD_TIP]: 1977,    // Buluş niteliği kod tipi (backend AppEnums.KodTipList.BULUS_NITELIGI)
  [KOD_ENUMS_KEYS.ONGORULEN_TESCIL_TURU_KOD_TIP]: 1684, // backend AppEnums.KodTipList.ONGORULEN_TESCIL_TURU
  [KOD_ENUMS_KEYS.PATENT_BASVURU_ASAMA_KOD_TIP]: 2028,  // backend AppEnums.KodTipList.PATENT_BASVURU_ASAMA_TIP
  [KOD_ENUMS_KEYS.RAPOR_VERI_LISTELERI_KOD_TIP]: 3146, // backend AppEnums.KodTipList.RAPOR_VERI_LISTELERI
  [KOD_ENUMS_KEYS.RAPOR_ISTATISTIK_LISTELERI_KOD_TIP]: 3153, // backend AppEnums.KodTipList.RAPOR_ISTATISTIK_LISTELERI
  [KOD_ENUMS_KEYS.CEVAP_TIP_KOD_TIP]: 3075,            // backend AppEnums.KodTipList.CEVAPTIP
  // Yeni enum ID'leri buraya eklenecek
};

/**
 * Production ortamı için kod ID'leri
 * Canlı ortamda kullanılacak gerçek ID değerleri
 */
const PROD_KOD_ENUMS: KodEnumConfig = {
  [KOD_ENUMS_KEYS.SERBEST_BULUS]: 2056,                    // Production ID'leri
  [KOD_ENUMS_KEYS.YENI_BULUS_BILDIRIMI]: 1978,
  [KOD_ENUMS_KEYS.BULUS_TESCIL_EDILDI]: 1980,             // bulusNiteligiCodeDto - TPE alanları gizlemek için
  [KOD_ENUMS_KEYS.BTYK_ARASTIRMA_ALANLARI]: 1504,
  [KOD_ENUMS_KEYS.SORU_TIP]: 2009,
  [KOD_ENUMS_KEYS.COKTAN_SECMELI]: 2010,
  [KOD_ENUMS_KEYS.KLASIK]: 2011,
  [KOD_ENUMS_KEYS.COKTAN_SECMELI_ANKET]: 2012,
  [KOD_ENUMS_KEYS.COKLU_CHECK]: 2013,
  [KOD_ENUMS_KEYS.COKTAN_SECMELI_ANKET_ARTI_KLASIK]: 2014,
  [KOD_ENUMS_KEYS.ANA_SORU_BAGLI_ALT_SORULAR_VAR]: 2067,
  [KOD_ENUMS_KEYS.KONTROLLERI_ATLA_ROL_ID]: 14,
  [KOD_ENUMS_KEYS.PATENT_DEGERLENDIRME_YETKILI_ROL_ID]: 10,
  [KOD_ENUMS_KEYS.FIRMA_LOGIN_TIP]: 3118,
  [KOD_ENUMS_KEYS.FIRMA_ASAMA_KOD_TIP]: 2028,
  [KOD_ENUMS_KEYS.KOD_ASAMA_DURUM_KOD_TIP]: 2028,
  [KOD_ENUMS_KEYS.KOD_ASAMA_YETKILI_KOD_TIP]: 3119,
  [KOD_ENUMS_KEYS.ODEME_DURUM_KOD_TIP]: 3124,
  [KOD_ENUMS_KEYS.YENI_KULLANICI_ROL_ID]: 7,
  [KOD_ENUMS_KEYS.BULUS_NITELIGI_KOD_TIP]: 1977,    // Buluş niteliği kod tipi (backend AppEnums.KodTipList.BULUS_NITELIGI)
  [KOD_ENUMS_KEYS.ONGORULEN_TESCIL_TURU_KOD_TIP]: 1684, // backend AppEnums.KodTipList.ONGORULEN_TESCIL_TURU
  [KOD_ENUMS_KEYS.PATENT_BASVURU_ASAMA_KOD_TIP]: 2028,  // backend AppEnums.KodTipList.PATENT_BASVURU_ASAMA_TIP
  [KOD_ENUMS_KEYS.RAPOR_VERI_LISTELERI_KOD_TIP]: 3146, // backend AppEnums.KodTipList.RAPOR_VERI_LISTELERI
  [KOD_ENUMS_KEYS.RAPOR_ISTATISTIK_LISTELERI_KOD_TIP]: 3153, // backend AppEnums.KodTipList.RAPOR_ISTATISTIK_LISTELERI
  [KOD_ENUMS_KEYS.CEVAP_TIP_KOD_TIP]: 3075,            // backend AppEnums.KodTipList.CEVAPTIP
  // Production ID'leri test'le farklıysa buraya ekle
};

/**
 * Environment'a göre uygun kod enum'larını export et
 * Test/Dev ortamında TEST_KOD_ENUMS, Production'da PROD_KOD_ENUMS kullanılır
 */
export const kodEnums = environment.production ? PROD_KOD_ENUMS : TEST_KOD_ENUMS;
